import fastDeepEqual from "fast-deep-equal/es6/react";
import {getCountryData, getStateById, getStateData} from "./geography";
import {
  difference,
  filter,
  find,
  findWhere,
  isEmpty,
  isNull,
  isUndefined,
  map,
  reject,
} from "underscore";

const nativeConditions = ["Severity", "IncidentType"];

export const enumSeparator = "~`";

export const buildConditionForMutation = (
  condition,
  includeRelation = false,
) => {
  const fieldType = condition.fieldType;
  const conditionId = condition.id;
  const values = condition.value;
  const comparator = includeRelation ? condition.relation : undefined;

  if (fieldType === "State") {
    return {
      conditionId: conditionId,
      comparator: comparator,
      value: values
        .map((item) => {
          return item.id;
        })
        .join(),
    };
  }
  if (fieldType === "Country") {
    return {
      conditionId: conditionId,
      comparator: comparator,
      value: values
        .map((item) => {
          return item.isoCode;
        })
        .join(),
    };
  }
  if (fieldType === "Boolean") {
    return {
      conditionId: conditionId,
      relation: includeRelation ? "eq" : undefined,
      value: String(values),
    };
  }
  if (fieldType === "Enum") {
    return {
      conditionId: conditionId,
      comparator: comparator,
      value: JSON.stringify(values),
    };
  }
  return {
    conditionId: conditionId,
    relation: comparator,
    value: values,
  };
};

export const buildConditionsForMutation = (
  conditions,
  includeRelation = false,
) => {
  const filteredConditions = conditions.filter(({fieldType}) => {
    return !nativeConditions.includes(fieldType) && fieldType !== undefined;
  });
  return filteredConditions.map((condition) =>
    buildConditionForMutation(condition, includeRelation),
  );
};

export const parseConditionValue = (condition) => {
  const fieldType = condition?.condition?.fieldType || condition.fieldType;
  const value = condition.value;

  if (fieldType === "Boolean") {
    return value === "true";
  } else if (fieldType === "Country") {
    return value.split(",").map((isoCode) => {
      return getCountryData(isoCode);
    });
  } else if (fieldType === "State") {
    return value.split(",").map((stateId) => {
      if (stateId.includes(":")) {
        const [stateCode, countryCode] = stateId.split(":");
        return getStateData(stateCode, countryCode);
      } else {
        return getStateById(stateId) || "";
      }
    });
  } else if (fieldType === "Enum") {
    try {
      return JSON.parse(value);
    } catch (e) {
      return value.split(enumSeparator).map((value) => ({value: value}));
    }
  }
  return value;
};

export const diffConditions = ({
  existingConditions,
  newConditions,
  validConditions,
  includeRelation = false,
  usePairForDelete = false,
}) => {
  const filteredNewConditions = filter(newConditions, ({fieldType}) => {
    return !nativeConditions.includes(fieldType);
  });
  if (
    !Array.isArray(existingConditions) ||
    !Array.isArray(filteredNewConditions)
  ) {
    throw Error("Invalid arguments!");
  }

  const invalidExistingConditions = filter(existingConditions, (condition) => {
    return !find(validConditions.conditions, (validCondition) => {
      return validCondition.id === condition.condition.id;
    });
  });

  const invalidNewConditions = [];
  const conditionsToCreate = filteredNewConditions.filter((newCondition) => {
    newCondition = buildConditionForMutation(newCondition, includeRelation);
    return !existingConditions.find((existingCondition) => {
      const oldOperator =
        existingCondition.relation ?? existingCondition.comparator;
      const newOperator = newCondition.relation ?? newCondition.comparator;
      const isInvalidCondition =
        isUndefined(newOperator) &&
        (isEmpty(newCondition?.value) || newCondition?.value === "[]");
      if (isInvalidCondition) {
        const matchingConditionFromSchema = findWhere(
          validConditions.conditions,
          {id: newCondition.conditionId},
        );
        invalidNewConditions.push({
          ...newCondition,
          field: matchingConditionFromSchema?.field ?? "",
        });
        return true;
      }
      return (
        newCondition.conditionId === existingCondition.condition.id &&
        newCondition.value === existingCondition.value &&
        oldOperator === newOperator
      );
    });
  });

  const conditionsToDelete = existingConditions.filter((existingCondition) => {
    return !Object.values(filteredNewConditions).find((newCondition) => {
      newCondition = buildConditionForMutation(newCondition, includeRelation);
      const oldOperator =
        existingCondition.relation ?? existingCondition.comparator;
      const newOperator = newCondition.relation ?? newCondition.comparator;
      return (
        newCondition.conditionId === existingCondition.condition.id &&
        newCondition.value === existingCondition.value &&
        oldOperator === newOperator
      );
    });
  });

  return {
    conditionsToCreate: buildConditionsForMutation(
      conditionsToCreate,
      includeRelation,
    ),
    conditionsToDelete: [
      ...map(conditionsToDelete, (condition) => {
        if (usePairForDelete) {
          return {
            AND: [
              {
                id: {equals: condition.id},
                conditionId: {equals: condition.conditionId},
              },
            ],
          };
        }
        return condition.id;
      }),
      ...map(invalidExistingConditions, (invalidCondition) => {
        if (usePairForDelete) {
          return {
            AND: [
              {
                id: {equals: invalidCondition.id},
                conditionId:
                  invalidCondition?.conditionId ?
                    {equals: invalidCondition?.conditionId} :
                    undefined,
              },
            ],
          };
        }
        return invalidCondition.id;
      }),
    ],
    invalidNewConditions,
  };
};

export const isQBLogicEqual = (newQBLogic, existingQBLogic) => {
  if (isEmpty(newQBLogic) && isEmpty(existingQBLogic)) {
    return true;
  }

  const newLogic = newQBLogic?.logic;
  const existingLogic = existingQBLogic?.logic;

  if (isEmpty(newLogic) && isEmpty(existingLogic)) {
    return true;
  }
  if (isEmpty(newLogic) || isEmpty(existingLogic)) {
    return false;
  }
  return fastDeepEqual(newLogic, existingLogic);
};

export const removeInvalidAttributesFromForm = (invalidItems, formMethods) => {
  if (isEmpty(invalidItems)) {
    return 0;
  }
  const currentFormAttributes = formMethods.getValues("attributes");
  const updatedFormAttributes = reject(
    currentFormAttributes,
    (currentFormAttribute) => {
      const doesInvalidConditionMatch = findWhere(invalidItems, {
        conditionId: currentFormAttribute?.conditionId,
      });
      return doesInvalidConditionMatch;
    },
  );
  const wasChangeMade = difference(
    currentFormAttributes,
    updatedFormAttributes,
  );
  if (!isEmpty(wasChangeMade)) {
    formMethods.setValue("attributes", updatedFormAttributes);
  }
  return wasChangeMade.length;
};
